import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";

import { Heading } from "@magnetic/heading";
import { Input } from "@magnetic/input";
import { Link } from "@magnetic/link";
import { Notification } from "@magnetic/notification";
import { Spinner } from "@magnetic/spinner";

import { CiscoLogo } from "src/common/ciscoLogo";
import { Button } from "src/components/buttons";
import { Fred } from "src/gen/rest/fred/FredService";
import { AddOrgRequest, AddOrgResponse } from "src/gen/schema/brig/bapi";
import { dispatchC3TenantSwitchEvent } from "src/utils/c3Utils";
import { Constants } from "src/utils/constants";
import { submitAddOrgViaFormPost } from "src/utils/crossGeoFormPost";
import { RegionSelect } from "src/utils/region/RegionSelect";
import {
  GeographicRegion,
  geographicRegionToGeo,
  getRegionInfo
} from "src/utils/region/regionTypes";
import {
  geographicRegionFromHostname,
  geographicRegionFromSessionContext
} from "src/utils/region/sessionGeo";

import { ApiError } from "./apiError";
import { LoginContext } from "./loginContext";

import "./crossGeoAddOrg.scss";

const getQueryParam = (key: string): string =>
  new URLSearchParams(window.location.search).get(key) || "";

/**
 * Auto-submit page for cross-geo add-org redirects.
 * Same landing query contract as signup: `orgName`, `geo` (target geo), and
 * optional `eula_version`. Region is required; we pre-fill from `geo` or the
 * current org’s `UserContext.geo`, then call **`Fred.addOrg`** (Fred handles
 * cross-geo redirect, then Brig). If neither region source is available, the
 * user must choose a region and submit.
 */
export const CrossGeoAddOrg = (): React.ReactElement => {
  const { session, userContexts, switchTenant, logout } =
    useContext(LoginContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | undefined>();
  const [orgName, setOrgName] = useState<string>(getQueryParam("orgName"));
  const [region, setRegion] = useState<GeographicRegion | undefined>(
    () => getRegionInfo(getQueryParam("geo") || undefined)?.code
  );
  const bootstrapDone = useRef<boolean>(false);

  useEffect(() => {
    setRegion((prev) => {
      if (prev !== undefined) {
        return prev;
      }
      return geographicRegionFromSessionContext(session, userContexts) ?? prev;
    });
  }, [session, userContexts]);

  const runAddOrg = useCallback(
    (name: string, targetRegion: GeographicRegion) => {
      const targetGeo = geographicRegionToGeo(targetRegion);
      const currentRegion =
        geographicRegionFromSessionContext(session, userContexts) ??
        geographicRegionFromHostname();
      const isCrossGeo =
        currentRegion !== undefined && targetRegion !== currentRegion;

      if (isCrossGeo) {
        submitAddOrgViaFormPost(name.trim(), targetGeo);
        return new Promise(() => {});
      }

      return Fred.addOrg(
        AddOrgRequest.create({ orgName: name.trim(), targetGeo })
      ).then((resp: AddOrgResponse) => {
        if (resp) {
          if (session?.isC3Crosslaunch) {
            dispatchC3TenantSwitchEvent(resp.cuiTenantId);
            return;
          }
          switchTenant(resp.orgId);
        }
      });
    },
    [session, userContexts, switchTenant]
  );

  const submitOrg = useCallback(() => {
    const trimmed = orgName.trim();
    if (!trimmed) {
      setError(
        ApiError.fromMessage("Organization name is missing from the request.")
      );
      return;
    }
    if (!region) {
      setError(ApiError.fromMessage("Select a geographic region."));
      return;
    }
    setError(undefined);
    setIsLoading(true);
    runAddOrg(trimmed, region)
      .catch((err: ApiError) => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [orgName, region, runAddOrg]);

  useEffect(() => {
    if (bootstrapDone.current) {
      return;
    }
    if (!orgName.trim()) {
      bootstrapDone.current = true;
      setError(
        ApiError.fromMessage("Organization name is missing from the request.")
      );
      setIsLoading(false);
      return;
    }

    const fromUrl = getRegionInfo(getQueryParam("geo") || undefined)?.code;
    const fromSession = geographicRegionFromSessionContext(
      session,
      userContexts
    );
    const resolved = fromUrl ?? fromSession;

    if (!resolved) {
      if (session?.tenantId && userContexts === undefined) {
        return;
      }
      setIsLoading(false);
      return;
    }

    bootstrapDone.current = true;
    setRegion(resolved);
    runAddOrg(orgName.trim(), resolved)
      .catch((err: ApiError) => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [orgName, session, userContexts, runAddOrg]);

  if (isLoading) {
    return (
      <div className="cross-geo-add-org cross-geo-add-org--loading">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="cross-geo-add-org">
      <div className="cross-geo-add-org__brand">
        <CiscoLogo />
        <span className="cross-geo-add-org__brand-divider" aria-hidden={true} />
        <span className="cross-geo-add-org__brand-name">
          {Constants.APP_NAME}
        </span>
      </div>
      {error && (
        <div className="cross-geo-add-org__banner-wrap">
          <Notification status="negative" data-qa="cross-geo-error-banner">
            Failed to create organization due to a network issue. Resubmit your
            request to create the organization or return to Sign in.
          </Notification>
        </div>
      )}
      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          submitOrg();
        }}
      >
        <div className="cross-geo-add-org__card">
          <div className="cross-geo-add-org__title-block">
            <Heading size="h2">Create new organization</Heading>
            <span className="cross-geo-add-org__subtitle">
              Once an organization is created it cannot be deleted.
            </span>
          </div>
          <div className="cross-geo-add-org__fields">
            <Input
              label="Organization name"
              name="orgName"
              value={orgName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOrgName(e.target.value)
              }
              placeholder="New org name"
              type="text"
              size="md"
              required={true}
              fixedWidth={true}
              autoComplete="off"
            />
            <RegionSelect value={region} onChange={setRegion} />
          </div>
          <div className="cross-geo-add-org__actions">
            <Link
              className="cross-geo-add-org__back-link"
              href="/"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                logout();
              }}
            >
              Back to Sign in
            </Link>
            <Button
              type="submit"
              size="md"
              name="retry-create-org"
              disabled={isLoading || !orgName.trim() || !region}
            >
              Create organization
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
